import { useCallback, useRef, useState, type CSSProperties } from 'react';
import { DIAS_TRIAL, NIVEIS, NIVEL_DESTAQUE, PLANOS } from '../dados/planos';
import type { Periodicidade } from '../lib/precos';
import { useEfeitoDeLayout } from '../hooks/use-efeito-de-layout';
import { Secao } from './secao';
import { Reveal, RevealGrupo } from './reveal';
import { PlanoCartao } from './plano-cartao';
import { CapturaInteresse } from './captura-interesse';

/**
 * A página de preços: TRÊS NÍVEIS × três prazos.
 *
 * ## Por que o prazo é um seletor, e o nível é um cartão
 * São nove combinações, e nove cartões numa página não se comparam — a pessoa
 * perde a conta do que está olhando. O que ela decide primeiro é o TAMANHO
 * ("quantas sociedades eu tenho?"); o prazo é a segunda pergunta, e uma só,
 * então cabe num seletor que troca os três preços de uma vez.
 *
 * Antes, a página tinha um nível só e três cartões de prazo: "um plano, três
 * formas de pagar". Não havia motivo para ninguém pagar mais — e sem isso não
 * há como crescer receita por cliente.
 */

const PRAZOS: { valor: Periodicidade; rotulo: string; economia?: string }[] = [
  { valor: 'MENSAL', rotulo: 'Mensal' },
  { valor: 'TRIMESTRAL', rotulo: 'Trimestral', economia: '10% off' },
  { valor: 'ANUAL', rotulo: 'Anual', economia: '30% off' },
];

export function Planos(): JSX.Element {
  // Abre no ANUAL: é o melhor preço por mês e é o prazo que a empresa quer
  // vender. Quem prefere mensal troca num clique e vê o número subir.
  const [prazo, setPrazo] = useState<Periodicidade>('ANUAL');

  /*
    A barra que desliza sob a opção escolhida.

    Mede o botão ativo e escreve largura e deslocamento em variáveis CSS — é o
    que uma biblioteca de motion faria com `layoutId`, feito à mão porque são
    quinze linhas e a alternativa custaria 34 kB no bundle.

    `useLayoutEffect` e não `useEffect`: a medição tem de acontecer ANTES da
    pintura, senão a barra aparece na posição errada por um quadro. E o
    reposicionamento é refeito no `resize` porque a largura dos botões muda com
    a da tela.
  */
  const trilho = useRef<HTMLDivElement | null>(null);
  const [barra, setBarra] = useState({ largura: 0, deslocamento: 0 });

  const medir = useCallback(() => {
    const no = trilho.current;
    const ativo = no?.querySelector<HTMLElement>('[aria-checked="true"]');
    if (!no || !ativo) return;
    setBarra({ largura: ativo.offsetWidth, deslocamento: ativo.offsetLeft });
  }, []);

  useEfeitoDeLayout(() => {
    medir();
    window.addEventListener('resize', medir);
    return () => {
      window.removeEventListener('resize', medir);
    };
  }, [medir, prazo]);

  const doPrazo = NIVEIS.map((nivel) =>
    PLANOS.find((p) => p.nome === nivel && p.periodicidade === prazo),
  ).filter((p) => p !== undefined);

  return (
    <Secao
      adiarRender
      alturaEstimada={1990}
      id="planos"
      rotulo="Planos"
      titulo={
        <>
          <span className="font-semibold">Três planos</span>, do escritório de dez
          empresas ao que não conta mais.
        </>
      }
      conduz={
        <>
          <span className="font-semibold tabular-nums">{DIAS_TRIAL}</span> dias de teste
          grátis em qualquer plano, sem cartão de crédito.
        </>
      }
    >
      {/*
        Grupo de RÁDIO, e não três botões soltos: são opções mutuamente
        exclusivas da mesma pergunta, e é isso que o leitor de tela precisa
        anunciar para a pessoa saber que escolher um desmarca o outro.
      */}
      <Reveal className="mt-10">
        <div
          ref={trilho}
          role="radiogroup"
          aria-label="Prazo de pagamento"
          className="seletor border-auth-borda/10 bg-auth-superficie/[0.03] rounded-auth-controle inline-flex border p-1"
          style={
            {
              '--largura': `${String(barra.largura)}px`,
              '--deslocamento-barra': `${String(barra.deslocamento)}px`,
            } as CSSProperties
          }
        >
          <span
            aria-hidden
            className="seletor__barra bg-auth-marca-forte rounded-auth-controle"
          />
          {PRAZOS.map((p) => (
            <button
              key={p.valor}
              type="button"
              role="radio"
              aria-checked={prazo === p.valor}
              onClick={() => setPrazo(p.valor)}
              /*
                Aperto no celular, folga a partir do `sm`.

                Os três prazos têm de caber numa LINHA só: a barra deslizante
                translada em X, então uma quebra de linha a deixaria sob o
                botão errado. A 375px o conjunto media 363px dentro de 327
                disponíveis, e o "30% off" do anual ficava fora da tela.

                E `py-3` na base: com `py-2` o botão media 36px de altura no
                celular — abaixo dos 44px de alvo de dedo, no controle que
                troca o preço. O `sm:py-2` devolve ao desktop exatamente a
                altura que ele sempre teve.
              */
              className={`rounded-auth-controle flex items-center gap-1.5 px-3 py-3 text-[13px] whitespace-nowrap transition-colors duration-200 sm:gap-2 sm:px-4 sm:py-2 sm:text-sm ${
                prazo === p.valor
                  ? 'text-auth-marca-contraste font-medium'
                  : 'text-auth-texto-secundario hover:text-auth-texto'
              }`}
            >
              {p.rotulo}
              {/* Branco PLENO no estado ativo: a 80% sobre `marca-forte` o
                  selo media 4,0:1 em fonte de 10px — reprovava o AA (4,5:1)
                  justamente no "30% off" que vende o anual. */}
              {p.economia ? (
                <span
                  className={`text-[10px] sm:text-xs ${
                    prazo === p.valor
                      ? 'text-auth-marca-contraste'
                      : 'text-auth-marca-hover'
                  }`}
                >
                  {p.economia}
                </span>
              ) : null}
            </button>
          ))}
        </div>
      </Reveal>

      <RevealGrupo className="palco-3d mt-8 grid items-stretch gap-5 sm:grid-cols-3">
        {doPrazo.map((plano) => (
          <Reveal key={plano.nome} direcao="profundidade">
            <PlanoCartao
              plano={plano}
              destaque={plano.nome === NIVEL_DESTAQUE}
              /* A âncora do desconto: o mensal do MESMO nível, achado no
                 catálogo — nunca uma coluna própria, pela mesma razão de
                 `equivalenteMensalCentavos` (ver `lib/precos.ts`). */
              referenciaMensalCentavos={
                PLANOS.find((p) => p.nome === plano.nome && p.periodicidade === 'MENSAL')
                  ?.precoCentavos
              }
            />
          </Reveal>
        ))}
      </RevealGrupo>

      {prazo !== 'MENSAL' ? (
        <Reveal className="mt-8">
          <p className="text-auth-texto-terciario max-w-2xl text-xs leading-relaxed">
            {prazo === 'ANUAL' ? 'O plano anual' : 'O plano trimestral'} é cobrado em uma
            parcela única no fechamento da assinatura — o valor "por mês" acima é a
            comparação com o preço mensal, não uma cobrança recorrente separada.
          </p>
        </Reveal>
      ) : null}

      <Reveal className="mx-auto mt-10 max-w-md">
        <p className="text-auth-texto-secundario mb-3 text-center text-sm">
          Ainda decidindo? Deixe seu e-mail e avisamos quando tiver novidade.
        </p>
        <CapturaInteresse origem="planos" />
      </Reveal>
    </Secao>
  );
}
